import { useAllProductsContext } from '@app/components/products/context/AllProductsContext';
import { TabList } from '@app/components/various/Tabs';
import {
  createSelection,
  getBuyerIdFromCurrentOrder,
  getDefaultStockType,
  getIsLoggedIn,
  getIsSeller,
  getIsStockSwitchNotificationEnabled,
  getStockTypeLabels,
} from '@app/ducks';
import { useConfirmationGuard, useHasProductsByStockType } from '@app/utils/hooks';
import { isDefined } from '@app/utils/is';
import { DeliveryWindow, Product, Tabs } from '@typings';
import { omit } from 'ramda';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import styles from './StockSwitcher.module.scss';

interface Props {
  onStockSwitchConfirmation?: VoidFunction;
  productsDetails?: Record<string, Product.Full>;
}

export const StockSwitcher = ({ onStockSwitchConfirmation, productsDetails }: Props) => {
  const { t } = useTranslation(['common', 'confirmationConfig']);
  const dispatch = useDispatch();
  const confirmationGuard = useConfirmationGuard();
  const buyerId = useSelector(getBuyerIdFromCurrentOrder);
  const isSeller = useSelector(getIsSeller);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const isStockSwitchNotificationEnabled = useSelector(getIsStockSwitchNotificationEnabled);
  const defaultStockType = useSelector(getDefaultStockType);
  const stockLabels = useSelector(getStockTypeLabels);
  const { changeFilters, filters } = useAllProductsContext();
  const hasProductsByStockType = useHasProductsByStockType({ productsDetails });
  const hasAddedProducts = hasProductsByStockType.preorder || hasProductsByStockType.stock;

  const shouldSwitchWithNotification = isStockSwitchNotificationEnabled && hasAddedProducts;

  const tabs: Tabs.Tab[] = [
    {
      id: 'stock',
      label: stockLabels.stock,
      testId: 'stockContentTab',
    },
    {
      id: 'preorder',
      label: stockLabels.preorder,
      testId: 'preorderContentTab',
    },
  ];

  const orderedTabs = defaultStockType === 'stock' ? tabs : [...tabs].reverse();

  const setStockType = React.useCallback(
    (stockType: DeliveryWindow.StockType) => {
      changeFilters({ onlyAvailable: 'no', stockType }, false);
    },
    [changeFilters],
  );

  const createNewSelection = React.useCallback(
    (stockType: DeliveryWindow.StockType) => {
      const buyerKey = isSeller ? { buyer: buyerId } : {};

      dispatch(
        createSelection({
          initialStockType: stockType,
          name: '',
          redirectPath: 'PRODUCTS_ORDER',
          ...buyerKey,
        }),
      );
    },
    [dispatch, buyerId, isSeller],
  );

  const stockChangeConfirmation = React.useCallback(
    (stockType: DeliveryWindow.StockType) => {
      onStockSwitchConfirmation?.();

      const stockName = stockLabels[stockType];
      const confirmationTexts = t('confirmationConfig:change_stock_type', { returnObjects: true, stockName });

      const extraButtonText = (
        <>
          <span className={styles.additionalButtonFull}>{confirmationTexts.extraButtonText.full}</span>
          <span className={styles.additionalButtonShort}>{confirmationTexts.extraButtonText.short}</span>
        </>
      );

      const content = (
        <div className={styles.notificationContent}>
          <p>
            {confirmationTexts.content.common.about_to_shop} {confirmationTexts.content.stockDescription[stockType]}
          </p>
          <p>{isLoggedIn ? confirmationTexts.content.common.create_new_selection : confirmationTexts.content.common.log_in}</p>
        </div>
      );

      return confirmationGuard(() => ({
        content,
        extraButtonText,
        loadingOnOk: true,
        okDisabled: !isLoggedIn,
        okType: 'info',
        onExtraButton: () => {
          setStockType(stockType);
        },
        onOk: () => {
          createNewSelection(stockType);
        },
        type: 'confirm',
        ...omit(['content', 'extraButtonText'], confirmationTexts),
      }))();
    },
    [stockLabels, t, isLoggedIn, confirmationGuard, setStockType, createNewSelection],
  );

  const handleStockChange = React.useCallback(
    (stockType: DeliveryWindow.StockType) => {
      const isSwitchingBack = hasProductsByStockType[stockType];

      if (!shouldSwitchWithNotification || isSwitchingBack) {
        return setStockType(stockType);
      }

      return stockChangeConfirmation(stockType);
    },
    [hasProductsByStockType, shouldSwitchWithNotification, stockChangeConfirmation, setStockType],
  );

  if (!isDefined(filters.stockType)) {
    return null;
  }

  return (
    <div className="w-full rounded-regular bg-white px-5 py-3">
      <div className="pb-3 text-neutral-60 heading-4">{t('common:stock_type')}</div>
      <TabList
        isFluid
        dataTestId="stockContentSwitcher"
        tablistName={t('common:stock_type')}
        tabs={orderedTabs}
        selectedId={filters.stockType}
        onChange={handleStockChange}
      />
    </div>
  );
};
